import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Pieni "Sinulla on keskeneräinen luonnos" -banneri näkyy
 * AppLayoutin yläosassa, jos käyttäjällä on localStoragessa tallennettu
 * laskuluonnos (apps2go.invoice_draft.new). Käyttäjä voi avata sen
 * yhdellä klikkauksella tai hylätä.
 */
const INVOICE_DRAFT_KEY = "apps2go.invoice_draft.new";
const DISMISS_KEY = "apps2go.draft_banner_dismissed";

export default function DraftRestoreBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!user || !user.email) return;
    if (location.pathname.startsWith("/invoicing/new")) return;
    try {
      const dismissed = sessionStorage.getItem(DISMISS_KEY);
      if (dismissed === "1") return;
      const raw = localStorage.getItem(INVOICE_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // näytetään vain jos jotain merkityksellistä on syötetty
      const meaningful =
        (parsed?.customer_name && parsed.customer_name.length > 0) ||
        (parsed?.items && parsed.items.some((i) => (i.description || "").trim()));
      if (meaningful) setDraft(parsed);
    } catch {
      setDraft(null);
    }
  }, [user, location.pathname]);

  const open = () => {
    navigate("/invoicing/new");
  };
  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDraft(null);
  };

  if (!draft) return null;

  const summary = draft.customer_name || "Nimetön asiakas";
  const total = draft.items?.reduce((s, i) => s + (parseFloat(i.qty || 0) * parseFloat(i.unit_price || 0)), 0) || 0;

  return (
    <div
      className="mb-4 rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/15 to-amber-500/5 p-3 flex items-center gap-3"
      data-testid="draft-restore-banner"
    >
      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        <FileText className="w-5 h-5 text-amber-300" />
      </div>
      <div className="flex-1 text-sm">
        <p className="font-medium text-amber-100">Sinulla on tallentamaton laskuluonnos</p>
        <p className="text-xs text-muted-foreground">
          {summary} – luonnoksen yhteissumma ≈ {total.toFixed(2)} €
        </p>
      </div>
      <Button
        size="sm"
        onClick={open}
        className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium"
        data-testid="draft-restore-open"
      >
        Avaa luonnos
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="w-8 h-8"
        onClick={dismiss}
        data-testid="draft-restore-dismiss"
        title="Piilota"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}
